// src/pages/DeviceManagement/components/FilterPanel/QuickFilters.jsx
// Quick filter chips for common device queries
// Part of Device Management Overhaul - Phase 1

import React from 'react';
import { Box, Chip, Typography, Tooltip } from '@mui/material';
import {
  WifiOff as OfflineIcon,
  LinkOff as UnlinkedIcon,
  WifiTethering as OnlineIcon,
  Schedule as RecentIcon
} from '@mui/icons-material';

const QuickFilters = ({ filters, onFilterChange, deviceCounts, isLoading = false }) => {
  const quickFilterOptions = [
    {
      key: 'offline',
      label: 'Offline',
      icon: <OfflineIcon fontSize="small" />,
      count: deviceCounts?.offline || 0,
      color: 'error',
      tooltip: 'Show only offline devices'
    },
    {
      key: 'unlinked',
      label: 'Unlinked',
      icon: <UnlinkedIcon fontSize="small" />,
      count: deviceCounts?.unlinked || 0,
      color: 'warning',
      tooltip: 'Show devices not assigned to any location'
    },
    {
      key: 'online',
      label: 'Online',
      icon: <OnlineIcon fontSize="small" />,
      count: deviceCounts?.online || 0,
      color: 'success',
      tooltip: 'Show only online devices'
    },
    {
      key: 'recentlySeen',
      label: 'Recently Seen',
      icon: <RecentIcon fontSize="small" />,
      count: deviceCounts?.recentlySeen || 0,
      color: 'info',
      tooltip: 'Show devices seen in the last hour'
    }
  ];

  const handleToggle = (filterKey) => {
    const newFilters = { ...filters };
    newFilters.quickFilters = newFilters.quickFilters || new Set();

    // Create a new Set to trigger re-render
    const newQuickFilters = new Set(newFilters.quickFilters);

    if (newQuickFilters.has(filterKey)) {
      newQuickFilters.delete(filterKey);
    } else {
      // Clear conflicting filters
      if (filterKey === 'online') {
        newQuickFilters.delete('offline');
      } else if (filterKey === 'offline') {
        newQuickFilters.delete('online');
      }
      newQuickFilters.add(filterKey);
    }

    newFilters.quickFilters = newQuickFilters;
    onFilterChange(newFilters);
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
        Quick Filters
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {quickFilterOptions.map((option) => {
          const isActive = filters.quickFilters?.has(option.key);
          const chipLabel = isLoading
            ? option.label
            : `${option.label} (${option.count})`;

          return (
            <Tooltip key={option.key} title={option.tooltip} arrow>
              <Chip
                icon={option.icon}
                label={chipLabel}
                onClick={() => handleToggle(option.key)}
                color={isActive ? option.color : 'default'}
                variant={isActive ? 'filled' : 'outlined'}
                clickable
                size="small"
                disabled={isLoading || (!isActive && option.count === 0)}
                sx={{
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 1,
                    transition: 'all 0.2s ease'
                  },
                  transition: 'all 0.2s ease',
                  fontWeight: isActive ? 600 : 400
                }}
              />
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
};

export default QuickFilters;